import React, { useEffect, useState } from "react";
import { sv } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarDays, Loader2 } from "lucide-react";
import { fetchAvailableSlots } from "../lib/api";
import { fmtDate, fmtDateSv } from "../lib/date";

function slotClass(slot, selectedTime) {
  if (selectedTime === slot.time) return "bg-[#1B45C4] text-white";
  if (slot.available) return "bg-white text-slate-700 ring-1 ring-slate-200 hover:ring-[#1B45C4]";
  return "bg-slate-100 text-slate-300 line-through cursor-not-allowed";
}

function isBlockedDay(d) {
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  return d < today || d.getDay() === 0; // past dates or Sundays
}

export default function BookingPicker({ date, setDate, time, setTime }) {
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setTime("");
    setSlotsLoading(true);
    fetchAvailableSlots(fmtDate(date))
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
    // setTime is a stable state setter; date is the only trigger we need.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const noneAvailable = slots.length > 0 && slots.every((s) => !s.available);
  const someAvailable = slots.some((s) => s.available);

  return (
    <div className="rounded-2xl bg-[#f0f7ff] p-4 ring-1 ring-[#dbeafe]">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <CalendarDays className="w-4 h-4 text-[#1B45C4]" /> Boka tid för besiktning <span className="font-normal text-slate-500">(valfritt)</span>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="flex h-11 items-center justify-between rounded-md border border-input bg-white px-3 text-sm text-slate-700 hover:border-[#1B45C4]">
              {date ? fmtDateSv(date) : "Välj datum"}
              <CalendarDays className="w-4 h-4 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white text-slate-900" align="start">
            <Calendar mode="single" locale={sv} selected={date} onSelect={setDate} disabled={isBlockedDay} initialFocus />
          </PopoverContent>
        </Popover>
        <div className="flex items-center text-sm text-slate-500">
          {!date && "Välj ett datum för att se lediga tider"}
          {date && slotsLoading && <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Hämtar tider...</span>}
          {date && !slotsLoading && noneAvailable && "Inga lediga tider – välj annat datum"}
        </div>
      </div>
      {date && !slotsLoading && someAvailable && (
        <div className="mt-3 flex flex-wrap gap-2">
          {slots.map((s) => (
            <button key={s.time} type="button" disabled={!s.available} onClick={() => setTime(s.time)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${slotClass(s, time)}`}>
              {s.time}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
